// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import React, { useState } from 'react';
import { Bell } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

export default function NotificationsBell({ bakerySubdomain }) {
  const { unreadNotifications, unreadCount, markAllNotificationsAsRead, markNotificationAsRead } = useNotifications();
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [expandedNotification, setExpandedNotification] = useState(null);

  const handleViewOrder = async (notification) => {
    if (!notification || !notification.orderId) {
      return;
    }

    setOpen(false);
    await markNotificationAsRead(notification.id);

    const ordersPath = bakerySubdomain
      ? `/${bakerySubdomain}/baker-orders`
      : '/home';

    navigate(ordersPath, {
      state: { focusOrderId: notification.orderId }
    });
  };

  const titleKey = language === 'fr' ? 'title_fr' : 'title_en';
  const bodyKey = language === 'fr' ? 'body_fr' : 'body_en';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="relative inline-flex items-center justify-center h-10 w-10 rounded-full bg-[var(--surface-card)] border border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--action-ghost-hover)] transition-colors"
        aria-label={language === 'fr' ? 'Notifications' : 'Notifications'}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-[var(--surface-card)] rounded-xl shadow-soft-lg border border-[var(--border-subtle)] z-40">
          <div className="px-3 py-2 border-b border-[var(--border-subtle)] flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
              {language === 'fr' ? 'Notifications' : 'Notifications'}
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllNotificationsAsRead}
                className="text-[11px] text-[var(--action-primary)] hover:text-[var(--action-primary-hover)]"
              >
                {language === 'fr'
                  ? 'Tout marquer comme lu'
                  : 'Mark all as read'}
              </button>
            )}
          </div>
          <div className="py-2">
            {unreadNotifications.length === 0 ? (
              <div className="px-3 py-2 text-xs text-[var(--text-secondary)]">
                {language === 'fr'
                  ? 'Aucune nouvelle notification'
                  : 'No new notifications'}
              </div>
            ) : (
              unreadNotifications.map((notification) => {
                const createdAt =
                  notification.createdAt && notification.createdAt.toDate
                    ? notification.createdAt.toDate()
                    : null;

                const title =
                  notification[titleKey] ||
                  notification.title_en ||
                  notification.title_fr ||
                  (language === 'fr' ? 'Notification' : 'Notification');

                const body =
                  notification[bodyKey] ||
                  notification.body_en ||
                  notification.body_fr ||
                  '';

                return (
                  <div
                    key={notification.id}
                    className="px-3 py-2 border-b border-[var(--border-subtle)] last:border-b-0 text-xs"
                  >
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        if (!notification.orderId) {
                          setExpandedNotification(notification);
                        }
                      }}
                    >
                      <div className="font-semibold text-[var(--text-primary)] mb-0.5">
                        {title}
                      </div>
                      <div className="text-[var(--text-secondary)] mb-1">
                        {body.length > 180 ? `${body.slice(0, 180)}…` : body}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      {notification.subscriptionUrl ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = notification.subscriptionUrl;
                            markNotificationAsRead(notification.id);
                          }}
                          className="text-[11px] font-semibold text-[var(--action-primary)] hover:text-[var(--action-primary-hover)]"
                        >
                          {language === 'fr'
                            ? 'Gérer l\'Abonnement'
                            : 'Manage Subscription'}
                        </button>
                      ) : notification.orderId ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewOrder(notification);
                          }}
                          className="text-[11px] font-semibold text-[var(--action-primary)] hover:text-[var(--action-primary-hover)]"
                        >
                          {language === 'fr'
                            ? 'Voir la commande'
                            : 'View order'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            markNotificationAsRead(notification.id);
                          }}
                          className="text-[11px] font-semibold text-[var(--action-primary)] hover:text-[var(--action-primary-hover)]"
                        >
                          {language === 'fr'
                            ? 'Marquer comme lu'
                            : 'Mark as read'}
                        </button>
                      )}
                      {createdAt && (
                        <span className="text-[11px] text-[var(--text-secondary)]">
                          {createdAt.toLocaleString(
                            language === 'fr' ? 'fr-CA' : 'en-US',
                            {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              const path = bakerySubdomain
                ? `/${bakerySubdomain}/notifications`
                : '/home';
              navigate(path);
            }}
            className="w-full text-[11px] text-center text-[var(--action-primary)] hover:text-[var(--action-primary-hover)] py-1 border-t border-[var(--border-subtle)]"
          >
            {language === 'fr'
              ? 'Voir toutes les notifications'
              : 'View all notifications'}
          </button>
        </div>
      )}

      {expandedNotification && (() => {
        const createdAt =
          expandedNotification.createdAt && expandedNotification.createdAt.toDate
            ? expandedNotification.createdAt.toDate()
            : null;
        const title =
          expandedNotification[titleKey] ||
          expandedNotification.title_en ||
          expandedNotification.title_fr ||
          (language === 'fr' ? 'Notification' : 'Notification');
        const body =
          expandedNotification[bodyKey] ||
          expandedNotification.body_en ||
          expandedNotification.body_fr ||
          '';

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="bg-[var(--surface-card)] rounded-2xl shadow-soft-lg max-w-md w-full mx-4 p-5">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                  {title}
                </h3>
                <button
                  type="button"
                  onClick={() => setExpandedNotification(null)}
                  className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                >
                  {language === 'fr' ? 'Fermer' : 'Close'}
                </button>
              </div>
              <div className="text-sm text-[var(--text-primary)] mb-3 whitespace-pre-line">
                {body}
              </div>
              {createdAt && (
                <div className="text-[11px] text-[var(--text-secondary)] text-right">
                  {createdAt.toLocaleString(
                    language === 'fr' ? 'fr-CA' : 'en-US',
                    {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
